/**
 * Record updates: the housekeeping half of what Inspector suggests — a title,
 * owners, a review cadence, a department, jurisdictions, a shelf, a framework,
 * a control mapping, the company profile. Not gaps, so not findings.
 *
 * Applying one changes a stored value. To make Apply reversible, the API keeps
 * the value as it was before the change and the value the change wrote
 * (`RecordApplication`). Undo restores the before-value, but only while the
 * value is still the one Apply wrote: once a person or another update has
 * changed it, restoring would overwrite their work, so Undo is refused.
 *
 * Values are normalised per kind so that "is it still what Apply wrote?" is a
 * plain comparison:
 *
 * | kind            | value                                          |
 * | --------------- | ---------------------------------------------- |
 * | rename          | the governance title, or null                  |
 * | review_cadence  | the review frequency, or null                  |
 * | department      | the department, or null                        |
 * | owner           | owner emails, lower-cased and sorted           |
 * | jurisdictions   | jurisdiction slugs, sorted                     |
 * | reclassify      | [documentKind] or [documentKind, implementsId] |
 * | framework       | the governance framework id, or null           |
 * | control_mapping | whether the control is linked (boolean)        |
 * | org_context     | the whole company profile text, or null        |
 *
 * @module platform-shared/recordUpdates
 */
import * as Schema from "effect/Schema";

export const RecordValue = Schema.Union([
  Schema.Null,
  Schema.String,
  Schema.Boolean,
  Schema.Array(Schema.String),
]);
export type RecordValue = typeof RecordValue.Type;

/** What Apply changed: the value before and the value it wrote. */
export const RecordApplication = Schema.Struct({
  before: RecordValue,
  after: RecordValue,
});
export type RecordApplication = typeof RecordApplication.Type;

/** Kinds whose Apply writes a value that Undo can restore. */
export const UNDOABLE_RECORD_KINDS = [
  "rename",
  "review_cadence",
  "department",
  "owner",
  "jurisdictions",
  "reclassify",
  "framework",
  "control_mapping",
  "org_context",
] as const;
export type UndoableRecordKind = (typeof UNDOABLE_RECORD_KINDS)[number];

export const isUndoableRecordKind = (kind: string): kind is UndoableRecordKind =>
  (UNDOABLE_RECORD_KINDS as ReadonlyArray<string>).includes(kind);

/** A list value in its comparable form. */
export const normaliseList = (values: ReadonlyArray<string>, lowerCase = false): ReadonlyArray<string> =>
  [...new Set(values.map((value) => (lowerCase ? value.trim().toLowerCase() : value.trim())).filter(Boolean))].sort();

/** Whether two record values are the same value. */
export function sameRecordValue(left: RecordValue, right: RecordValue): boolean {
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right)) return false;
    return left.length === right.length && left.every((value, index) => value === right[index]);
  }
  // Empty text and no text are the same absence.
  const blank = (value: RecordValue) => value === null || (typeof value === "string" && value.trim() === "");
  if (blank(left) && blank(right)) return true;
  return left === right;
}

export const RECORD_UNDO_CHANGED_MESSAGE =
  "This value has changed since the update was applied, so Undo would overwrite a newer change. Change it by hand instead.";
export const RECORD_UNDO_UNAVAILABLE_MESSAGE =
  "This update was applied before Undo existed, so there is no earlier value to restore.";

/**
 * Why Undo cannot run, or null when it can. Undo is for an applied update
 * whose stored value is still the one Apply wrote.
 */
export function recordUndoRefusal(input: {
  readonly kind: string;
  readonly status: string;
  readonly application: RecordApplication | null;
  readonly current: RecordValue | undefined;
}): string | null {
  if (input.status !== "applied") return "Only an applied update can be undone.";
  if (!isUndoableRecordKind(input.kind)) return "This kind of update cannot be undone.";
  if (input.application === null) return RECORD_UNDO_UNAVAILABLE_MESSAGE;
  if (input.current === undefined) return "The record this update changed no longer exists.";
  if (!sameRecordValue(input.current, input.application.after)) return RECORD_UNDO_CHANGED_MESSAGE;
  return null;
}

/** A readable form of a record value, for chips and toasts. */
export function formatRecordValue(kind: string, value: RecordValue): string {
  if (value === null) return "Not set";
  if (typeof value === "boolean") return kind === "control_mapping" ? (value ? "Linked" : "Not linked") : String(value);
  // `Array.isArray` does not narrow a readonly array, so test for the string.
  if (typeof value !== "string") {
    if (kind === "reclassify") return value[0] === "procedure" ? "Procedure" : "Policy";
    return value.length === 0 ? "None" : value.join(", ");
  }
  if (value.trim() === "") return "Not set";
  if (kind === "review_cadence") return CADENCE_LABELS[value] ?? value;
  return value;
}

const CADENCE_LABELS: Readonly<Record<string, string>> = {
  none: "No review",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannual: "Every six months",
  yearly: "Annual",
};
